// UniFi API Service - For local UniFi Controllers
class UniFiAPIService {
  constructor() {
    this.config = null;
    this.baseURL = '';
    this.username = '';
    this.password = '';
    this.site = 'default';
    this.csrfToken = null;
    this.sessionId = null;
  }

  // Initialize the service with configuration
  initialize(config) {
    this.config = config;
    this.baseURL = config.host.startsWith('http') ? config.host : `https://${config.host}`;
    this.username = config.username;
    this.password = config.password;
    this.site = config.site || 'default';
  }

  // Load configuration from localStorage
  loadConfig() {
    try {
      const savedConfig = localStorage.getItem('unifiConfig');
      if (savedConfig) {
        this.config = JSON.parse(savedConfig);
        this.baseURL = this.config.host.startsWith('http') ? this.config.host : `https://${this.config.host}`;
        this.username = this.config.username;
        this.password = this.config.password;
        this.site = this.config.site || 'default';
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading UniFi config:', error);
      return false;
    }
  }

  // Save configuration to localStorage
  saveConfig(config) {
    try {
      const configToSave = {
        ...config,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem('unifiConfig', JSON.stringify(configToSave));
      this.initialize(configToSave);
      return true;
    } catch (error) {
      console.error('Error saving UniFi config:', error);
      return false;
    }
  }

  // Login to UniFi Controller
  async login() {
    try {
      console.log('Logging into UniFi Controller...');
      
      // Get CSRF token first
      const csrfResponse = await fetch(`${this.baseURL}/api/auth/csrf`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (csrfResponse.ok) {
        const csrfData = await csrfResponse.json();
        this.csrfToken = csrfData.csrf_token;
      }

      // Login
      const loginResponse = await fetch(`${this.baseURL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken || '',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: this.username,
          password: this.password,
          remember: true
        })
      });

      if (loginResponse.ok) {
        const loginData = await loginResponse.json();
        this.sessionId = loginData.session_id;
        console.log('UniFi Controller login successful');
        return { success: true, data: loginData };
      } else {
        throw new Error(`Login failed: ${loginResponse.status} ${loginResponse.statusText}`);
      }
    } catch (error) {
      console.error('UniFi Controller login error:', error);
      return { success: false, error: error.message };
    }
  }

  // Generic API request method
  async makeRequest(endpoint, options = {}) {
    try {
      const url = `${this.baseURL}/api/s/${this.site}${endpoint}`;
      console.log(`UniFi Controller API Request: ${url}`);
      
      const defaultHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      if (this.csrfToken) {
        defaultHeaders['X-CSRF-Token'] = this.csrfToken;
      }

      const response = await fetch(url, {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
        credentials: 'include',
      });

      console.log(`UniFi Controller API Response: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`UniFi Controller API Error: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('UniFi Controller API Request Error:', error);
      return { success: false, error: error.message };
    }
  }

  // Test connection
  async testConnection() {
    try {
      console.log('Testing UniFi Controller connection...');
      
      // Try to login first
      const loginResult = await this.login();
      if (!loginResult.success) {
        return loginResult;
      }

      // Test API access
      const result = await this.makeRequest('/stat/device');
      
      if (result.success) {
        console.log('UniFi Controller connection successful:', result.data);
      } else {
        console.error('UniFi Controller connection failed:', result.error);
      }
      
      return result;
    } catch (error) {
      console.error('UniFi Controller test connection error:', error);
      return { success: false, error: error.message };
    }
  }

  // Get devices
  async getDevices() {
    return await this.makeRequest('/stat/device');
  }

  // Get clients
  async getClients() {
    return await this.makeRequest('/stat/sta');
  }

  // Get system status
  async getSystemStatus() {
    return await this.makeRequest('/stat/sysinfo');
  }

  // Get access devices (if available)
  async getAccessDevices() {
    return await this.makeRequest('/stat/access-device');
  }

  // Get access logs (if available)
  async getAccessLogs() {
    return await this.makeRequest('/stat/access-log');
  }

  // Get protect cameras (if available)
  async getProtectCameras() {
    return await this.makeRequest('/stat/camera');
  }

  // Check if configured
  isConfigured() {
    return !!(this.config && this.baseURL && this.username && this.password);
  }

  // Check if connected
  isConnected() {
    return !!(this.sessionId && this.csrfToken);
  }

  // Get configuration
  getConfig() {
    return this.config;
  }
}

export default UniFiAPIService; 